// src/pages/Organizations.jsx
import { useState, useEffect, useCallback } from "react";
import {
  Trash2, Users, Copy, RefreshCw, CheckCircle2, AlertTriangle, X, ShieldAlert,
  Building2, BadgeCheck, Clock,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import { useSidebarCollapsed } from "../utils/useSidebarCollapsed";
import { redirectToLogin } from "../utils/auth";

// Denser SOC/Datadog-style panel — flat dark surface, hairline sky border,
// no heavy glass/shadow.
const panel = "rounded-md border border-sky-900/30 bg-[#0a1224]/70";

// Shared input shell so every form field across the page is consistent.
const inputClass =
  "w-full rounded-md border border-sky-900/30 bg-neutral-900/60 px-3 py-2 text-[13px] text-white outline-none focus:border-sky-500/50 transition-colors disabled:opacity-50";

// Primary CTA — solid flat sky-600 (matches Profile / Login).
const primaryBtn =
  "inline-flex items-center gap-1.5 rounded-md bg-sky-600 hover:bg-sky-500 px-3.5 py-1.5 text-[13px] font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

// Secondary CTA (ghost) — outline with sky tint for test / show / less-prominent actions.
const secondaryBtn =
  "inline-flex items-center gap-1.5 rounded-md border border-sky-600/40 bg-sky-500/10 hover:bg-sky-500/20 px-3.5 py-1.5 text-[13px] font-medium text-sky-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

// Uppercase form label — matches the Profile redesign.
const fieldLabel = "block mb-1 text-[11px] uppercase tracking-wider text-neutral-500 font-medium";

/* ───────────────── Dismissible Alert ───────────────── */
function AlertBanner({ kind = "success", children, onClose }) {
  const isSuccess = kind === "success";
  const border = isSuccess ? "border-emerald-500/30" : "border-red-500/30";
  const bg = isSuccess ? "bg-emerald-500/10" : "bg-red-500/10";
  const text = isSuccess ? "text-emerald-200" : "text-red-200";
  const Icon = isSuccess ? CheckCircle2 : AlertTriangle;
  return (
    <div role="alert" className={`relative rounded-lg ${border} ${bg} px-3 py-2 text-sm ${text}`}>
      <div className="flex items-start gap-2 pr-6">
        <Icon className="h-4 w-4 mt-[2px]" />
        <div className="leading-relaxed">{children}</div>
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        className="absolute right-1.5 top-1.5 rounded-md p-1 text-inherit hover:bg-white/10"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/* ───────────────── Confirm Regenerate Modal ───────────────── */
function ConfirmRegenerateModal({ open, onClose, onConfirm, isLoading }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      aria-modal="true"
      role="dialog"
      aria-labelledby="regen-title"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative z-[101] w-full max-w-md rounded-md border border-sky-900/40 bg-[#0a1224]/95 p-5 shadow-[0_0_20px_rgba(56,189,248,0.08)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 shrink-0 rounded-md bg-sky-500/15 border border-sky-600/30 flex items-center justify-center">
            <svg width="18" height="18" viewBox="0 0 24 24" className="text-sky-300">
              <path fill="currentColor" d="M13 2v6h6v2h-6v6h-2v-6H5V8h6V2z"/>
            </svg>
          </div>
          <h3 id="regen-title" className="text-base font-semibold text-white">
            Regenerate API Key?
          </h3>
        </div>

        <p className="mt-3 text-sm text-neutral-300">
          This will <span className="text-white font-medium">immediately replace</span> the current key.
          All clients using the old key will stop working until updated.
        </p>

        <div className="mt-4 rounded-lg bg-neutral-900/50 border border-neutral-800 px-3 py-2 text-xs text-neutral-400">
          Tip: Export your current environment variables before rotating.
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border border-neutral-700 bg-transparent px-3 py-2 text-sm text-neutral-300 hover:bg-white/5"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-lg border border-sky-600/40 bg-sky-500/20 px-3 py-2 text-sm text-sky-200 hover:bg-sky-500/30 disabled:opacity-60"
          >
            {isLoading && (
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" className="opacity-20" stroke="currentColor" strokeWidth="4" fill="none"/>
                <path d="M4 12a8 8 0 018-8" className="opacity-80" stroke="currentColor" strokeWidth="4" fill="none"/>
              </svg>
            )}
            Regenerate
          </button>
        </div>

        <button
          onClick={onClose}
          className="absolute right-3 top-3 rounded-md p-1 text-neutral-400 hover:text-white hover:bg-white/10"
          aria-label="Close"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

/* ───────────────── Confirm Remove Modal ───────────────── */
function ConfirmRemoveModal({ open, email, onClose, onConfirm, isLoading }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      aria-modal="true"
      role="dialog"
      aria-labelledby="remove-title"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative z-[101] w-full max-w-md rounded-md border border-red-500/30 bg-[#0a1224]/95 p-5 shadow-[0_0_20px_rgba(239,68,68,0.15)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 shrink-0 rounded-md bg-red-500/15 border border-red-500/30 flex items-center justify-center">
            <svg width="18" height="18" viewBox="0 0 24 24" className="text-red-300">
              <path fill="currentColor" d="M9 3v1H4v2h1v12a3 3 0 003 3h8a3 3 0 003-3V6h1V4h-5V3H9zm9 3v12a1 1 0 01-1 1H9a1 1 0 01-1-1V6h10zM9 8h2v8H9V8zm4 0h2v8h-2V8z"/>
            </svg>
          </div>
          <h3 id="remove-title" className="text-base font-semibold text-white">
            Remove user?
          </h3>
        </div>

        <p className="mt-3 text-sm text-neutral-300">
          This action will remove <span className="text-white font-medium">{email}</span> from your organization.
        </p>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border border-neutral-700 bg-transparent px-3 py-2 text-sm text-neutral-300 hover:bg-white/5"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="inline-flex items-center gap-2 rounded-lg border border-red-500/40 bg-red-500/20 px-3 py-2 text-sm text-red-100 hover:bg-red-500/30 disabled:opacity-60"
          >
            {isLoading && (
              <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" className="opacity-20" stroke="currentColor" strokeWidth="4" fill="none"/>
                <path d="M4 12a8 8 0 018-8" className="opacity-80" stroke="currentColor" strokeWidth="4" fill="none"/>
              </svg>
            )}
            Remove
          </button>
        </div>

        <button
          onClick={onClose}
          className="absolute right-3 top-3 rounded-md p-1 text-neutral-400 hover:text-white hover:bg-white/10"
          aria-label="Close"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

/* ───────────────── Reusable CopyBox ───────────────── */
function CopyBox({ label, value }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      console.error("Copy failed", e);
    }
  };
  return (
    <div>
      <div className="block mb-1 text-[11px] uppercase tracking-wider text-neutral-500 font-medium">
        {label}
      </div>
      <div className="flex items-center justify-between gap-2 rounded-md bg-neutral-900/60 border border-sky-900/30 px-3 py-2">
        <code className="font-mono text-[12px] text-neutral-200 break-all">{value}</code>
        <button
          onClick={handleCopy}
          className="shrink-0 rounded px-2 py-1 text-[10px] uppercase tracking-wider font-mono text-neutral-400 hover:text-white hover:bg-white/[0.06] transition inline-flex items-center gap-1"
          title="Copy to clipboard"
        >
          {copied ? "Copied" : (<><Copy className="h-3 w-3" />Copy</>)}
        </button>
      </div>
    </div>
  );
}

export default function Organizations() {
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Gating flags from server (Option C)
  const [planLimited, setPlanLimited] = useState(false);
  const [gateMessage, setGateMessage] = useState(null);

  const [organization, setOrganization] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [savingSso, setSavingSso] = useState(false);

  // API tab states
  const [apiInfo, setApiInfo] = useState(null); // { api_key, remaining_calls }
  const [showKey, setShowKey] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [regenLoading, setRegenLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [apiSuccess, setApiSuccess] = useState(null);

  // Users (LEFT pane) – now scoped
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [usersSuccess, setUsersSuccess] = useState(null);
  const [removingEmail, setRemovingEmail] = useState(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);

  // Invite (RIGHT pane) – now scoped
  const [inviteError, setInviteError] = useState(null);
  const [inviteSuccess, setInviteSuccess] = useState(null);

  // License tab states
  const [licenseLoading, setLicenseLoading] = useState(true);
  const [licenseError, setLicenseError] = useState(null);

  // Tabs
  const [activeTab, setActiveTab] = useState("license"); // license | adduser | api | sso

  // SSO form
  const [tenantId, setTenantId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState(""); // never prefilled
  const redirectUrl = "https://platform.whiteintel.io/login";
  const [ssoVerified, setSsoVerified] = useState(false);
  const [forceSso, setForceSso] = useState(false);
  const [ssoError, setSsoError] = useState(null);
  const [ssoSuccess, setSsoSuccess] = useState(null);

  // JIRA FORM

  const [jiraIssueType, setJiraIssueType] = useState("Bug"); // "Issue" | "Bug"
  const [jiraUrl, setJiraUrl] = useState("");
  const [jiraUsername, setJiraUsername] = useState("");
  const [jiraToken, setJiraToken] = useState("");
  const [jiraProjectKey, setJiraProjectKey] = useState("");
  const [jiraActive, setJiraActive] = useState(false);
  const [jiraDisableLoading, setJiraDisableLoading] = useState(false);
// NEW: test connection UX state
  const [jiraTestLoading, setJiraTestLoading] = useState(false);
  const [jiraBanner, setJiraBanner] = useState(null); // { kind: 'success'|'error', message: string }
  const [jiraSaveLoading, setJiraSaveLoading] = useState(false);

//SLACK
const [slackWebhookUrl, setSlackWebhookUrl] = useState('');
const [slackChannelName, setSlackChannelName] = useState('');
const [slackBotName, setSlackBotName] = useState('');

const [slackBanner, setSlackBanner] = useState(null);
const [slackActive, setSlackActive] = useState(false);
const [slackSaveLoading, setSlackSaveLoading] = useState(false);
const [slackTestLoading, setSlackTestLoading] = useState(false);
const [slackDisableLoading, setSlackDisableLoading] = useState(false);


const handleDisableSlack = async () => {
  setSlackBanner(null);
  setSlackDisableLoading(true);

  try {
    const res = await fetch("https://whiteintel.io/api/regular/app/v2/slack/disable_slack.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwtToken()}`,
      },
    });

    const json = await res.json().catch(() => ({}));

    if (res.ok && json?.success) {
      setSlackActive(false);
      setSlackBanner({ kind: "success", message: "Slack integration disabled." });
    } else {
      setSlackBanner({
        kind: "error",
        message: json?.error || `Failed to disable Slack integration (HTTP ${res.status})`,
      });
    }
  } catch (e) {
    setSlackBanner({
      kind: "error",
      message: e?.message || "Network error while disabling Slack integration.",
    });
  } finally {
    setSlackDisableLoading(false);
  }
};



const testSlackConnection = async () => {
  setSlackBanner(null);

  const url = slackWebhookUrl?.trim();
  if (!url) {
    setSlackBanner({
      kind: "error",
      message: "Please enter a Slack webhook URL to test.",
    });
    return;
  }

  setSlackTestLoading(true);

  try {
    const res = await fetch("https://whiteintel.io/api/regular/app/v2/slack/test_slack.php", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getJwtToken()}`,
      },
      body: JSON.stringify({ slack_webhook_url: url }),
    });

    const json = await res.json().catch(() => ({}));

    if (res.ok && json?.success) {
      setSlackBanner({ kind: "success", message: "Slack webhook tested successfully." });
    } else {
      setSlackBanner({
        kind: "error",
        message: json?.error || `Slack test failed (HTTP ${res.status})`,
      });
    }
  } catch (e) {
    setSlackBanner({
      kind: "error",
      message: e?.message || "Network error while testing Slack webhook.",
    });
  } finally {
    setSlackTestLoading(false);
  }
};

  const handleSaveSlack = async () => {
  setSlackBanner(null); // clear existing banner

  const url = slackWebhookUrl?.trim();
  const channel = slackChannelName?.trim();
  const bot = slackBotName?.trim();

  if (!url) {
    setSlackBanner({
      kind: "error",
      message: "Please provide a valid Slack Webhook URL.",
    });
    return;
  }

  setSlackSaveLoading(true);

  try {
    const res = await fetch(
      "https://whiteintel.io/api/regular/app/v2/slack/configure_slack.php",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({
          slack_webhook_url: url,
          channel_name: channel || undefined,
          bot_name: bot || undefined,
        }),
      }
    );

    const json = await res.json().catch(() => ({}));

    if (res.ok && json?.success) {
      setSlackBanner({ kind: "success", message: "Slack integration saved." });
      setSlackActive(true);
    } else {
      setSlackBanner({
        kind: "error",
        message: json?.error || `Failed to save Slack integration (HTTP ${res.status}).`,
      });
    }
  } catch (e) {
    setSlackBanner({
      kind: "error",
      message: e?.message || "Network error while saving Slack integration.",
    });
  } finally {
    setSlackSaveLoading(false);
  }
};


async function handleDisableJira() {
  if (planLimited || jiraDisableLoading) return;
  setJiraBanner(null);
  setJiraDisableLoading(true);

  try {
    const res = await fetch(
      "https://whiteintel.io/api/regular/app/v2/jira/disable_jira.php",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({}), // body unused by your endpoint, but keep JSON to satisfy guard
      }
    );

    // auth redirect handling consistent with the rest of the page
    if ([401, 419, 440].includes(res.status)) {
      redirectToLogin();
      return;
    }

    const json = await res.json().catch(() => ({}));

    if (!res.ok || json?.success !== true) {
      throw new Error(json?.error || `Failed to disable Jira (HTTP ${res.status})`);
    }

    setJiraActive(false);
    setJiraBanner({ kind: "success", message: "Jira integration disabled." });
    setJiraUsername("");
    setJiraUrl("");
    setJiraToken("");
    setJiraProjectKey("");
    setJiraIssueType("");
  } catch (e) {
    setJiraBanner({ kind: "error", message: e?.message || "Failed to disable Jira." });
  } finally {
    setJiraDisableLoading(false);
  }
}



  const handleSaveJira = async () => {
  setJiraBanner(null);

  const url = jiraUrl?.trim();
  const user = jiraUsername?.trim();
  const token = jiraToken?.trim();
  const project = jiraProjectKey?.trim();
  const type = jiraIssueType?.trim();

  if (!url || !user || !token || !project || !type) {
    setJiraBanner({
      kind: "error",
      message: "Please fill Jira URL, Username, API Key, Project Key and Ticket Type.",
    });
    return;
  }

  setJiraSaveLoading(true);
  try {
    const res = await fetch(
      "https://whiteintel.io/api/regular/app/v2/jira/configure_jira.php",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`, // uses your existing helper
        },
        body: JSON.stringify({
          jira_url: url,
          jira_username: user,
          jira_token: token,
          jira_project: project,
          jira_ticket_type: type,
        }),
      }
    );

    const json = await res.json().catch(() => ({}));

    if (res.ok && json?.success) {
      setJiraActive(true);
      setJiraBanner({ kind: "success", message: "Jira integration saved." });
    } else {
      setJiraBanner({
        kind: "error",
        message: json?.error || `Failed to save Jira integration (HTTP ${res.status}).`,
      });
    }
  } catch (e) {
    setJiraBanner({
      kind: "error",
      message: e?.message || "Network error while saving Jira integration.",
    });
  } finally {
    setJiraSaveLoading(false);
  }
};


  const testJiraConnection = async () => {
  // clear any previous banner
  setJiraBanner(null);

  // quick client-side checks
  if (!jiraUrl?.trim() || !jiraUsername?.trim() || !jiraToken?.trim()) {
    setJiraBanner({ kind: "error", message: "Please fill Jira URL, Username and API Key first." });
    return;
  }

  setJiraTestLoading(true);
  try {
    const res = await fetch(
      "https://whiteintel.io/api/regular/app/v2/jira/test_jira.php",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getJwtToken()}`,
        },
        body: JSON.stringify({
          jira_url: jiraUrl.trim(),
          jira_username: jiraUsername.trim(),
          jira_token: jiraToken.trim(),
        }),
      }
    );

    const json = await res.json().catch(() => ({}));

    if (res.ok && json?.success) {
      const who = json.displayName || json.accountId || jiraUsername;
      setJiraBanner({
        kind: "success",
        message: `Jira connection successful. Authenticated as ${who}.`,
      });
    } else {
      setJiraBanner({
        kind: "error",
        message: json?.error || `Test failed (HTTP ${res.status}).`,
      });
    }
  } catch (e) {
    setJiraBanner({
      kind: "error",
      message: e?.message || "Network error while testing Jira connection.",
    });
  } finally {
    setJiraTestLoading(false);
  }
};


  function getJwtToken() {
    try {
      return (
        localStorage.getItem("token") ||
        sessionStorage.getItem("jwtToken") ||
        window.WHITEINTEL_TOKEN ||
        ""
      );
    } catch {
      return "";
    }
  }

  const removeUser = async (email) => {
    if (planLimited || !email) return;

    try {
      setUsersError(null);
      setUsersSuccess(null);
      setRemovingEmail(email);

      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/organizations/delete_user.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
          body: JSON.stringify({ email }),
        }
      );

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }

      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        throw new Error(json?.error || `Failed to remove user (HTTP ${res.status})`);
      }

      setUsers((prev) => prev.filter((u) => u.email !== email));
      setUsersSuccess("User removed successfully.");
    } catch (e) {
      setUsersError(e?.message || "Failed to remove user.");
    } finally {
      setRemovingEmail(null);
      setRemoveOpen(false);
      setPendingEmail(null);
    }
  };

  async function saveSsoConfig() {
    setSsoError(null);
    setSsoSuccess(null);

    if (!tenantId?.trim() || !clientId?.trim() || !redirectUrl?.trim()) {
      setSsoError("Please fill all SSO fields (Tenant ID, Client ID, Redirect URL).");
      return;
    }

    setSavingSso(true);
    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/organizations/configure_sso.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(getJwtToken() ? { Authorization: `Bearer ${getJwtToken()}` } : {}),
          },
          body: JSON.stringify({
            provider: "azure",
            tenant_id: tenantId.trim(),
            client_id: clientId.trim(),
            client_secret: (clientSecret ?? "").trim(), // optional for public clients
            redirect_uri: redirectUrl.trim(),
            enforce_sso: !!forceSso,
          }),
        }
      );

      if ([401, 419, 440].includes(res.status)) {
        setSsoError("Your session has expired. Please sign in again.");
        return;
      }

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        setSsoError(json?.error || `Failed to save SSO settings (HTTP ${res.status}).`);
        return;
      }

      if (json?.success) {
        setSsoVerified(!!json.verified || true);
        setSsoSuccess("SSO configuration saved successfully.");
      } else {
        setSsoError(json?.error || "Could not verify SSO settings. Please check values and try again.");
      }
    } catch (err) {
      setSsoError(err?.message || "Network error while saving SSO settings.");
    } finally {
      setSavingSso(false);
    }
  }

  /* ── Populate tenantId/clientId when organization arrives ── */
  useEffect(() => {
    if (!organization) return;

    const t = organization.azure_tenant_id ?? "";
    const c = organization.azure_client_id ?? organization.client_id ?? "";

    setTenantId(t);
    setClientId(c);
    setClientSecret("");

    setForceSso(!!(organization.sso_enabled ?? organization.enforce_sso));
    setSsoVerified(Boolean(t && c));
  }, [organization]);

  /* ── Fetch license (Option C) ── */
  useEffect(() => {
    const fetchLicense = async () => {
      try {
        setLicenseLoading(true);
        setLicenseError(null);
        setPlanLimited(false);
        setGateMessage(null);

        const res = await fetch(
          "https://whiteintel.io/api/regular/app/v2/organizations/get_license.php",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getJwtToken()}`,
            },
          }
        );

        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }

        const data = await res.json();

        if (data?.plan_limited) {
          setPlanLimited(true);
          setGateMessage(data?.message || "Upgrade required for unlocking full features.");
          setOrganization(data?.organization || null);
          setApiInfo(data?.api || null);
          return;
        }

        const org = data?.organization;
        if (!org) throw new Error("Invalid license response");

        setOrganization(org);
        setApiInfo(data?.api || null);

        if(data?.jira?.active){
        setJiraUrl(data?.jira?.jira_url);
        setJiraUsername(data?.jira?.jira_username);
        setJiraToken(data?.jira?.jira_token);
        setJiraProjectKey(data?.jira?.jira_project);
        setJiraIssueType(data?.jira?.jira_ticket_type);
        setJiraActive(data?.jira?.active);
        }

        if(data?.slack?.active)
        {

          setSlackWebhookUrl(data?.slack?.slack_webhook_url);
          setSlackChannelName(data?.slack?.channel_name);
          setSlackBotName(data?.slack?.bot_name);
          setSlackActive(data?.slack?.active);
        }

      } catch (err) {
        console.error(err);
        setLicenseError("Failed to load license info.");
        setPlanLimited(false);
      } finally {
        setLicenseLoading(false);
      }
    };

    fetchLicense();
  }, []);

  /* ── Fetch users if not gated ── */
  useEffect(() => {
    if (planLimited) {
      setUsersLoading(false);
      return;
    }
    const fetchUsers = async () => {
      try {
        setUsersLoading(true);
        const res = await fetch(
          "https://whiteintel.io/api/regular/app/v2/organizations/get_users.php",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${getJwtToken()}`,
            },
          }
        );

        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }

        const data = await res.json();
        setUsers(data.users || []);
      } catch (err) {
        console.error(err);
        setUsersError("Failed to load users.");
      } finally {
        setUsersLoading(false);
      }
    };

    fetchUsers();
  }, [planLimited]);

  /* ── Add User ── */
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("analyst");
  const addUser = async () => {
    if (!newEmail.trim() || planLimited) return;
    try {
      setInviteError(null);
      setInviteSuccess(null);

      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/organizations/invite_user.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
          body: JSON.stringify({
            email: newEmail.trim(),
            role: newRole,
          }),
        }
      );

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }

      const json = await res.json();
      if (!res.ok) throw new Error(json?.error || "Add user failed");

      setUsers((prev) => [
        {
          email: newEmail.trim(),
          organization_role: newRole,
          joined_at: new Date().toISOString(),
        },
        ...prev,
      ]);
      setNewEmail("");
      setNewRole("analyst");
      setInviteSuccess("User invited successfully.");
    } catch (e) {
      setInviteError(e.message || "Failed to add user");
    }
  };

  /* ── API: Copy + Regenerate ── */
  const handleCopyKey = useCallback(async () => {
    if (!apiInfo?.api_key || planLimited) return;
    try {
      await navigator.clipboard.writeText(apiInfo.api_key);
      setCopiedKey(true);
      setTimeout(() => setCopiedKey(false), 1500);
    } catch (e) {
      console.error("Copy failed", e);
    }
  }, [apiInfo?.api_key, planLimited]);

  const handleRegenerateKey = useCallback(async () => {
    if (planLimited) return;
    setRegenLoading(true);
    setApiError(null);
    setApiSuccess(null);
    try {
      const res = await fetch(
        "https://whiteintel.io/api/regular/app/v2/apikeys/regenerate_api.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getJwtToken()}`,
          },
          body: JSON.stringify({}),
        }
      );

      if ([401, 419, 440].includes(res.status)) {
        redirectToLogin();
        throw new Error(`AUTH_REDIRECT_${res.status}`);
      }

      const data = await res.json();
      if (!res.ok || !data?.apikey) {
        throw new Error(data?.error || data?.message || "Failed to regenerate key");
      }

      setApiInfo((prev) => ({ ...(prev || {}), api_key: data.apikey }));
      setShowKey(false);
      setApiSuccess("API key regenerated successfully.");
    } catch (err) {
      console.error(err);
      setApiError(err.message || "Unknown error");
    } finally {
      setRegenLoading(false);
    }
  }, [planLimited]);

  const maskedKey = apiInfo?.api_key?.replace(/.(?=.{4})/g, "•") || "—";

  const handleUpgrade = () => {
    try {
      window.location.href = "/upgrades";
    } catch {
      window.open("mailto:sales@whiteintel.io", "_blank");
    }
  };

  // Org-level derived bits for the identity strip and the License tab.
  let orgDaysLeft = null;
  if (organization?.expires_at) {
    const today = new Date();
    const exp = new Date(organization.expires_at);
    orgDaysLeft = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
  }
  const orgExpired = orgDaysLeft !== null && orgDaysLeft < 0;
  const orgExpiringSoon = orgDaysLeft !== null && orgDaysLeft >= 0 && orgDaysLeft <= 30;
  const seatsUsed = users.length;
  const seatsTotal = Number(organization?.seats || 0);

  return (
    <div className="min-h-screen bg-[#000414] text-white">
      {/* Header spacer */}
      <header
        className="bg-[#0a1224]/85 border-b border-sky-800/40 backdrop-blur sticky top-0 z-40 shadow-[0_6px_16px_-8px_rgba(0,0,0,0.7)]"
        style={{ marginLeft: collapsed ? "56px" : "224px" }}
      >
        <div className="px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-[20px] font-bold leading-tight tracking-tight">Organization settings</h1>
          </div>
        </div>
      </header>

      <div className="flex">
        <Sidebar
          collapsed={collapsed}
          setCollapsed={setCollapsed}
          mobileOpen={mobileOpen}
          setMobileOpen={setMobileOpen}
          activeKey="watchlists"
          onChange={() => {}}
        />

        {/* Content */}
        <main className="relative flex-1 px-4 md:px-6 pb-10">
          {/* Plan-limited card just under the header */}
          {planLimited && (
            <div className="mt-3 max-w-2xl">
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-amber-100">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="h-5 w-5 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-[12px] font-semibold uppercase tracking-wider text-amber-100">Organization features are locked</div>
                    <div className="text-[13px] opacity-90 mt-1.5 leading-relaxed">
                      Organization features aren't available on your current plan. Upgrade to Enterprise or Threat Intelligence to unlock multi-seat access, SSO, Jira and Slack integrations.
                    </div>
                    <div className="mt-3">
                      <button onClick={handleUpgrade} className={primaryBtn}>
                        Upgrade plan
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ============ ORG IDENTITY STRIP ============
              Compact header strip showing which org you're managing, its
              plan, seat usage, and expiry status — mirrors the Profile page. */}
          {!planLimited && organization && (
            <div className={`${panel} mt-4 px-5 py-4 flex flex-wrap items-center gap-4`}>
              <div className="min-w-0 flex-1">
                <div className="text-[14px] font-semibold text-white truncate">{organization.name || "Your organization"}</div>
                <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-neutral-500 font-mono uppercase tracking-wider">
                  <Building2 className="h-3 w-3" />
                  <span className="truncate">Organization workspace</span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/30 bg-sky-500/10 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-sky-300">
                  <BadgeCheck className="h-3 w-3" />
                  {organization.plan || "—"}
                </span>
                {seatsTotal > 0 && (
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-neutral-700 bg-neutral-900/50 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-neutral-300">
                    <Users className="h-3 w-3" />
                    {seatsUsed}/{seatsTotal} seats
                  </span>
                )}
                {orgDaysLeft !== null && (
                  <span className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider ${
                    orgExpired
                      ? "border-rose-500/40 bg-rose-500/10 text-rose-300"
                      : orgExpiringSoon
                      ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
                      : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                  }`}>
                    <Clock className="h-3 w-3" />
                    {orgExpired ? "Expired" : `${orgDaysLeft}d left`}
                  </span>
                )}
              </div>
            </div>
          )}

          {!planLimited && (
            <div className={`mt-4 grid grid-cols-1 lg:grid-cols-[1.25fr_0.75fr] gap-4 transition`}>
              {/* LEFT: Users list */}
              <div className={`${panel} h-[70vh] flex flex-col`}>
                <div className="px-4 py-3 border-b border-sky-900/30 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="h-3.5 w-3.5 text-sky-300/80" />
                    <h3 className="text-[12px] font-semibold text-neutral-200 uppercase tracking-wider">Users</h3>
                  </div>
                  <span className="text-[10px] font-mono text-neutral-500 uppercase tracking-wider">
                    {usersLoading ? "Loading…" : `${users.length} ${users.length === 1 ? "user" : "users"}`}
                  </span>
                </div>

                {/* Optional banners for LEFT pane */}
                {usersError && (
                  <div className="px-4 pt-3">
                    <AlertBanner kind="error" onClose={() => setUsersError(null)}>
                      {usersError}
                    </AlertBanner>
                  </div>
                )}
                {usersSuccess && (
                  <div className="px-4 pt-3">
                    <AlertBanner kind="success" onClose={() => setUsersSuccess(null)}>
                      {usersSuccess}
                    </AlertBanner>
                  </div>
                )}

                <div className="flex-1 overflow-auto">
                  <table className="w-full text-[12px]">
                    <thead className="sticky top-0 bg-[#0a1224]/95 backdrop-blur-sm">
                      <tr className="text-neutral-500 border-b border-sky-900/30">
                        <th className="px-4 py-2.5 text-left font-semibold uppercase tracking-wider text-[10px]">Email</th>
                        <th className="px-4 py-2.5 text-left font-semibold uppercase tracking-wider text-[10px]">Role</th>
                        {/* Joined date is lowest-priority info — hide on small
                            screens so the email column gets the freed width. */}
                        <th className="hidden sm:table-cell px-4 py-2.5 text-left font-semibold uppercase tracking-wider text-[10px]">Joined</th>
                        <th className="px-4 py-2.5 text-right font-semibold uppercase tracking-wider text-[10px]"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-sky-900/20">
                      {usersError ? (
                        <tr>
                          <td colSpan={4} className="px-4 py-10 text-center text-red-400">{usersError}</td>
                        </tr>
                      ) : users.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="px-4 py-10 text-center text-neutral-500">
                            {planLimited ? "Upgrade required to view users." : "No members yet — invite one from the right."}
                          </td>
                        </tr>
                      ) : (
                        users.map((u, idx) => {
                          const isAdmin = (u.organization_role || "").toLowerCase() === "admin";
                          return (
                            <tr key={`${u.email}-${idx}`} className="hover:bg-white/[0.02] transition-colors">
                              {/* Single-line email so the table can scroll
                                  horizontally on mobile instead of breaking the
                                  address into one-char-per-row. Tooltip carries
                                  the full address for accessibility. */}
                              <td
                                className="px-4 py-2.5 text-white font-mono whitespace-nowrap max-w-[60vw] sm:max-w-none overflow-hidden text-ellipsis"
                                title={u.email}
                              >
                                {u.email}
                              </td>
                              <td className="px-4 py-2.5">
                                <span className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider ${
                                  isAdmin
                                    ? "border-sky-500/30 bg-sky-500/10 text-sky-300"
                                    : "border-neutral-700 bg-neutral-900/50 text-neutral-300"
                                }`}>
                                  {u.organization_role || "—"}
                                </span>
                              </td>
                              <td className="hidden sm:table-cell px-4 py-2.5 text-neutral-400 font-mono tabular-nums">
                                {u.joined_at ? u.joined_at.split(" ")[0] : "—"}
                              </td>
                              <td className="px-4 py-2.5 text-right">
                                <button
                                  onClick={() => { setPendingEmail(u.email); setRemoveOpen(true); }}
                                  className="inline-flex items-center gap-1 text-[11px] text-rose-300 hover:text-rose-200 disabled:opacity-60"
                                  title="Remove user"
                                  disabled={planLimited}
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                  Remove
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* RIGHT: Tabs */}
              <div className={`${panel} h-[70vh] flex flex-col`}>
                {/* Underline tab strip — same Datadog-style pattern as Profile. */}
                <div className="flex items-stretch border-b border-sky-900/30 px-2 overflow-x-auto">
                  {[
                    { id: "license", label: "License" },
                    { id: "adduser", label: "Users" },
                    { id: "api",     label: "API" },
                    { id: "sso",     label: "SSO" },
                    { id: "Jira",    label: "Jira" },
                    { id: "Slack",   label: "Slack" },
                  ].map((t) => (
                    <button
                      key={t.id}
                      onClick={() => setActiveTab(t.id)}
                      disabled={planLimited && t.id !== "license"}
                      className={`relative shrink-0 px-3 py-3 text-[12px] font-semibold uppercase tracking-wider transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                        activeTab === t.id ? "text-white" : "text-neutral-500 hover:text-neutral-300"
                      }`}
                    >
                      {t.label}
                      {activeTab === t.id && (
                        <span className="absolute inset-x-2 -bottom-px h-0.5 bg-sky-400" />
                      )}
                    </button>
                  ))}
                </div>

                {/* Tab content */}
                <div className="flex-1 overflow-auto p-5">
                  {activeTab === "license" && (
                    <div className="space-y-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-sky-300/70 font-semibold mb-1">License details</div>
                        <div className="text-[12px] text-neutral-500">Plan entitlements, seats, and quotas for your organization.</div>
                      </div>

                      {licenseLoading ? (
                        <div className="text-[12px] text-neutral-500">Loading license…</div>
                      ) : licenseError ? (
                        <AlertBanner kind="error" onClose={() => setLicenseError(null)}>
                          {licenseError}
                        </AlertBanner>
                      ) : organization ? (
                        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 text-[13px]">
                          <div className="sm:col-span-2">
                            <dt className={fieldLabel}>Organization</dt>
                            <dd className="text-white">{organization.name || "—"}</dd>
                          </div>
                          <div>
                            <dt className={fieldLabel}>Plan</dt>
                            <dd>
                              <span className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/30 bg-sky-500/10 px-2 py-0.5 text-[11px] font-mono uppercase tracking-wider text-sky-300">
                                <BadgeCheck className="h-3 w-3" />
                                {organization.plan || "—"}
                              </span>
                            </dd>
                          </div>
                          <div>
                            <dt className={fieldLabel}>Expires</dt>
                            <dd className="flex items-center gap-2">
                              <span className="text-white font-mono tabular-nums">{organization.expires_at || "—"}</span>
                              {orgDaysLeft !== null && (
                                <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider ${
                                  orgExpired
                                    ? "border-rose-500/40 bg-rose-500/10 text-rose-300"
                                    : orgExpiringSoon
                                    ? "border-amber-500/30 bg-amber-500/10 text-amber-300"
                                    : "border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                                }`}>
                                  {orgExpired ? "Expired" : `${orgDaysLeft}d`}
                                </span>
                              )}
                            </dd>
                          </div>
                          <div>
                            <dt className={fieldLabel}>Seats</dt>
                            <dd className="text-white font-mono tabular-nums">{seatsUsed}/{organization.seats || "—"}</dd>
                          </div>
                          <div>
                            <dt className={fieldLabel}>CSV export limit</dt>
                            <dd className="text-white font-mono tabular-nums">{organization.csv_export_limit ?? "—"}</dd>
                          </div>
                          <div>
                            <dt className={fieldLabel}>API limit</dt>
                            <dd className="text-white font-mono tabular-nums">{organization.api_limit ?? "—"}</dd>
                          </div>

                          <div className="sm:col-span-2 pt-3 border-t border-sky-900/20">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-sky-300/70 font-semibold mb-2">Identifiers</div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                              {[
                                ["Domain", organization.domain_identifiers],
                                ["Email", organization.email_identifiers],
                                ["IP", organization.ip_identifiers],
                                ["Computer", organization.computername_identifiers],
                              ].map(([label, value]) => (
                                <div key={label}>
                                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">{label}</div>
                                  <div className="text-white font-mono tabular-nums">{value ?? "—"}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </dl>
                      ) : (
                        <div className="text-[12px] text-neutral-500">
                          {planLimited ? "Upgrade required to view license details." : "No license data."}
                        </div>
                      )}

                      <div className="pt-3 border-t border-sky-900/20 flex items-center justify-between">
                        <p className="text-[11px] text-neutral-500 font-mono">
                          Platform version <span className="text-neutral-300">4.0.5</span>
                        </p>
                        <button onClick={handleUpgrade} className={secondaryBtn}>
                          Manage plan
                        </button>
                      </div>
                    </div>
                  )}

                  {activeTab === "adduser" && (
                    <div className="space-y-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-sky-300/70 font-semibold mb-1">Invite a teammate</div>
                        <div className="text-[12px] text-neutral-500">They'll receive an activation email and join under the selected role.</div>
                      </div>

                      {inviteError && (
                        <AlertBanner kind="error" onClose={() => setInviteError(null)}>
                          {inviteError}
                        </AlertBanner>
                      )}
                      {inviteSuccess && (
                        <AlertBanner kind="success" onClose={() => setInviteSuccess(null)}>
                          {inviteSuccess}
                        </AlertBanner>
                      )}

                      <div>
                        <label className={fieldLabel}>Work email</label>
                        <input
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          placeholder="user@company.com"
                          className={inputClass}
                          required
                          disabled={planLimited}
                        />
                      </div>

                      <div>
                        <label className={fieldLabel}>Role</label>
                        <select
                          value={newRole}
                          onChange={(e) => setNewRole(e.target.value)}
                          className={inputClass}
                          disabled={planLimited}
                        >
                          <option value="admin">Admin</option>
                          <option value="analyst">Analyst</option>
                        </select>
                      </div>

                      <div className="flex items-center justify-end pt-1">
                        <button
                          onClick={addUser}
                          disabled={!newEmail.trim() || planLimited}
                          className={primaryBtn}
                        >
                          Send invite
                        </button>
                      </div>
                    </div>
                  )}

                  {activeTab === "api" && (
                    <div className="space-y-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-sky-300/70 font-semibold mb-1">Organization API key</div>
                        <div className="text-[12px] text-neutral-500">Shared across all members — rotate after offboarding or suspected exposure.</div>
                      </div>

                      {apiError && (
                        <AlertBanner kind="error" onClose={() => setApiError(null)}>{apiError}</AlertBanner>
                      )}
                      {apiSuccess && (
                        <AlertBanner kind="success" onClose={() => setApiSuccess(null)}>{apiSuccess}</AlertBanner>
                      )}

                      <div>
                        <div className={fieldLabel}>Key</div>
                        <div className="flex items-center justify-between gap-2 bg-neutral-900/60 border border-sky-900/30 rounded-md px-3 py-2">
                          <span className="font-mono text-[12px] tracking-wider text-neutral-200 select-none break-all">
                            {showKey ? apiInfo?.api_key || "—" : (apiInfo?.api_key ? maskedKey : "—")}
                          </span>
                          <div className="flex items-center gap-1 shrink-0">
                            <button
                              onClick={() => setShowKey((v) => !v)}
                              className="rounded px-2 py-1 text-[10px] uppercase tracking-wider font-mono text-neutral-400 hover:text-white hover:bg-white/[0.06] transition disabled:opacity-50"
                              disabled={!apiInfo?.api_key || planLimited}
                            >
                              {showKey ? "Hide" : "Show"}
                            </button>
                            <button
                              onClick={handleCopyKey}
                              className="rounded px-2 py-1 text-[10px] uppercase tracking-wider font-mono text-neutral-400 hover:text-white hover:bg-white/[0.06] transition disabled:opacity-50"
                              disabled={!apiInfo?.api_key || planLimited}
                            >
                              {copiedKey ? "Copied" : "Copy"}
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-[12px]">
                        <span className="text-neutral-500 uppercase tracking-wider text-[10px] font-medium">Remaining calls today</span>
                        <span className="text-white font-mono tabular-nums">
                          {typeof apiInfo?.remaining_calls === "number" ? apiInfo.remaining_calls.toLocaleString() : "—"}
                        </span>
                      </div>

                      <div className="flex items-center justify-between flex-wrap gap-2 pt-3 border-t border-sky-900/20">
                        <p className="text-[12px] text-neutral-500 max-w-md">
                          Rotating immediately invalidates the old key. Update every integration before regenerating.
                        </p>
                        <button
                          onClick={() => setConfirmOpen(true)}
                          disabled={regenLoading || planLimited}
                          className={secondaryBtn}
                        >
                          <RefreshCw className={`h-3.5 w-3.5 ${regenLoading ? "animate-spin" : ""}`} />
                          {regenLoading ? "Regenerating…" : "Regenerate"}
                        </button>
                      </div>
                    </div>
                  )}

                  {activeTab === "Jira" && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.22em] text-sky-300/70 font-semibold mb-1">Jira integration</div>
                          <div className="text-[12px] text-neutral-500">Create tickets in your Jira project for new watchlist events.</div>
                        </div>
                        {jiraActive && (
                          <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-emerald-300">
                            <CheckCircle2 className="h-3 w-3" />
                            Active
                          </span>
                        )}
                      </div>

                      {jiraBanner && (
                        <AlertBanner kind={jiraBanner.kind} onClose={() => setJiraBanner(null)}>
                          {jiraBanner.message}
                        </AlertBanner>
                      )}

                      <div>
                        <label className={fieldLabel}>Jira URL</label>
                        <input
                          type="text" value={jiraUrl}
                          onChange={(e) => setJiraUrl(e.target.value)}
                          placeholder="company.atlassian.net"
                          className={inputClass} disabled={planLimited}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className={fieldLabel}>Jira email</label>
                          <input
                            type="text" value={jiraUsername}
                            onChange={(e) => setJiraUsername(e.target.value)}
                            placeholder="user@company.com"
                            className={inputClass} disabled={planLimited}
                          />
                        </div>
                        <div>
                          <label className={fieldLabel}>API key</label>
                          <input
                            type="password" value={jiraToken}
                            onChange={(e) => setJiraToken(e.target.value)}
                            placeholder="Jira API token"
                            className={inputClass} disabled={planLimited}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className={fieldLabel}>Project code</label>
                          <input
                            type="text" value={jiraProjectKey}
                            onChange={(e) => setJiraProjectKey(e.target.value)}
                            placeholder="e.g. OPS"
                            className={inputClass} disabled={planLimited}
                          />
                        </div>
                        <div>
                          <label className={fieldLabel}>Ticket type</label>
                          <select
                            value={jiraIssueType}
                            onChange={(e) => setJiraIssueType(e.target.value)}
                            className={inputClass} disabled={planLimited}
                          >
                            <option value="Bug">Bug</option>
                            <option value="Task">Task</option>
                          </select>
                        </div>
                      </div>

                      <p className="text-[12px] text-amber-300/80 flex items-start gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        Grant this account only the minimum privileges needed to create tickets.
                      </p>

                      <div className="flex items-center justify-end gap-2 pt-3 border-t border-sky-900/20">
                        {jiraActive && (
                          <button
                            onClick={handleDisableJira}
                            disabled={planLimited || jiraDisableLoading}
                            className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 px-3 py-1.5 text-[13px] font-medium text-rose-200 disabled:opacity-50 transition-colors"
                          >
                            {jiraDisableLoading ? "Disabling…" : "Disable"}
                          </button>
                        )}
                        <button
                          onClick={testJiraConnection}
                          className={secondaryBtn}
                          disabled={planLimited || jiraTestLoading || !jiraUrl?.trim() || !jiraUsername?.trim() || !jiraToken?.trim()}
                        >
                          {jiraTestLoading ? "Testing…" : "Test connection"}
                        </button>
                        <button
                          onClick={handleSaveJira}
                          className={primaryBtn}
                          disabled={
                            planLimited || jiraSaveLoading ||
                            !jiraUrl.trim() || !jiraUsername.trim() || !jiraToken.trim() ||
                            !jiraProjectKey.trim() || !jiraIssueType.trim()
                          }
                        >
                          {jiraSaveLoading ? "Saving…" : "Save configuration"}
                        </button>
                      </div>
                    </div>
                  )}


                  {activeTab === "Slack" && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.22em] text-sky-300/70 font-semibold mb-1">Slack integration</div>
                          <div className="text-[12px] text-neutral-500">Post watchlist events into a Slack channel via incoming webhook.</div>
                        </div>
                        {slackActive && (
                          <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-emerald-300">
                            <CheckCircle2 className="h-3 w-3" />
                            Active
                          </span>
                        )}
                      </div>

                      {slackBanner && (
                        <AlertBanner kind={slackBanner.kind} onClose={() => setSlackBanner(null)}>
                          {slackBanner.message}
                        </AlertBanner>
                      )}

                      <div>
                        <label className={fieldLabel}>Webhook URL</label>
                        <input
                          type="text" value={slackWebhookUrl}
                          onChange={(e) => setSlackWebhookUrl(e.target.value)}
                          placeholder="https://hooks.slack.com/services/..."
                          className={inputClass} disabled={planLimited}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className={fieldLabel}>Channel <span className="text-neutral-600 normal-case">(optional)</span></label>
                          <input
                            type="text" value={slackChannelName}
                            onChange={(e) => setSlackChannelName(e.target.value)}
                            placeholder="#alerts"
                            className={inputClass} disabled={planLimited}
                          />
                        </div>
                        <div>
                          <label className={fieldLabel}>Bot name <span className="text-neutral-600 normal-case">(optional)</span></label>
                          <input
                            type="text" value={slackBotName}
                            onChange={(e) => setSlackBotName(e.target.value)}
                            placeholder="WhiteIntelBot"
                            className={inputClass} disabled={planLimited}
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-end gap-2 pt-3 border-t border-sky-900/20">
                        {slackActive && (
                          <button
                            onClick={handleDisableSlack}
                            disabled={planLimited || slackDisableLoading}
                            className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 hover:bg-rose-500/20 px-3 py-1.5 text-[13px] font-medium text-rose-200 disabled:opacity-50 transition-colors"
                          >
                            {slackDisableLoading ? "Disabling…" : "Disable"}
                          </button>
                        )}
                        <button
                          onClick={testSlackConnection}
                          className={secondaryBtn}
                          disabled={planLimited || slackTestLoading || !slackWebhookUrl?.trim()}
                        >
                          {slackTestLoading ? "Testing…" : "Test connection"}
                        </button>
                        <button
                          onClick={handleSaveSlack}
                          className={primaryBtn}
                          disabled={planLimited || slackSaveLoading || !slackWebhookUrl?.trim()}
                        >
                          {slackSaveLoading ? "Saving…" : "Save configuration"}
                        </button>
                      </div>
                    </div>
                  )}


                  {activeTab === "sso" && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.22em] text-sky-300/70 font-semibold mb-1">Microsoft SSO</div>
                          <div className="text-[12px] text-neutral-500">Provision your organization through Microsoft Entra ID (Azure AD).</div>
                        </div>
                        {ssoVerified && (
                          <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-emerald-300">
                            <CheckCircle2 className="h-3 w-3" />
                            Configured
                          </span>
                        )}
                      </div>

                      {ssoError && (
                        <AlertBanner kind="error" onClose={() => setSsoError(null)}>{ssoError}</AlertBanner>
                      )}
                      {ssoSuccess && (
                        <AlertBanner kind="success" onClose={() => setSsoSuccess(null)}>{ssoSuccess}</AlertBanner>
                      )}

                      <div>
                        <label className={fieldLabel}>Tenant ID</label>
                        <input
                          type="text" value={tenantId}
                          onChange={(e) => setTenantId(e.target.value)}
                          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                          className={inputClass} disabled={planLimited}
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className={fieldLabel}>Client ID</label>
                          <input
                            type="text" value={clientId}
                            onChange={(e) => setClientId(e.target.value)}
                            placeholder="Azure App Client ID"
                            className={inputClass} disabled={planLimited}
                          />
                        </div>
                        <div>
                          <label className={fieldLabel}>Client secret</label>
                          <input
                            type="text" value={clientSecret}
                            onChange={(e) => setClientSecret(e.target.value)}
                            placeholder="Azure Client Secret"
                            className={inputClass} disabled={planLimited}
                          />
                        </div>
                      </div>

                      <CopyBox label="Redirect / Callback URL" value={redirectUrl} />
                      <CopyBox label="Entity ID (Identifier)" value="https://whiteintel.io" />

                      <label className="flex items-start gap-2 mt-3 cursor-pointer">
                        <input
                          type="checkbox" disabled={planLimited}
                          checked={forceSso}
                          onChange={(e) => setForceSso(e.target.checked)}
                          className="mt-0.5"
                        />
                        <span className="text-[13px] text-neutral-300">
                          <span className="font-medium text-white">Enforce SSO only</span>
                          <span className="block text-[11px] text-amber-300/80 mt-0.5">
                            Disables password login. Only enable after a successful test — admins risk being locked out.
                          </span>
                        </span>
                      </label>

                      <div className="flex items-center justify-end pt-3 border-t border-sky-900/20">
                        <button
                          onClick={saveSsoConfig}
                          className={primaryBtn}
                          disabled={planLimited || savingSso}
                        >
                          {savingSso ? "Saving…" : "Save configuration"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </main>
      </div>

      <ConfirmRegenerateModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        isLoading={regenLoading}
        onConfirm={async () => {
          setConfirmOpen(false);
          await handleRegenerateKey();
        }}
      />

      <ConfirmRemoveModal
        open={removeOpen}
        email={pendingEmail}
        onClose={() => { if (!removingEmail) { setRemoveOpen(false); setPendingEmail(null); } }}
        onConfirm={() => removeUser(pendingEmail)}
        isLoading={removingEmail === pendingEmail}
      />
    </div>
  );
}
